import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/client';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, Filter, X, ChevronDown, ChevronRight } from 'lucide-react';

/* ───────────────────────────────────────────────
   TaxonomyFilterSidebar — Cascading filter panel
   
   Hierarchy is enforced:
   - Level 1 (e.g. Faculty): all tags shown
   - Level 2 (e.g. Department): only children of
     selected Level 1 tags (or all if none selected)
   - Level 3 (e.g. Program): only children of
     selected Level 2 tags (or cascaded from Level 1)
   
   Deselecting a parent clears its descendant selections.
   ─────────────────────────────────────────────── */

const TaxonomyFilterSidebar = ({ orgId, selectedTagIds = [], onFilterChange }) => {
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState(new Set(selectedTagIds));

  // Build a lookup: tagId -> tag object (across all types)
  const tagMap = useMemo(() => {
    const map = {};
    filters.forEach(tt => {
      tt.tags.forEach(t => { map[t.id] = { ...t, tagTypeName: tt.name, tagTypeDisplayOrder: tt.displayOrder }; });
    });
    return map;
  }, [filters]);

  // Build a parent->children lookup
  const childrenOf = useMemo(() => {
    const map = {};
    Object.values(tagMap).forEach(t => {
      if (t.parentTagId) {
        if (!map[t.parentTagId]) map[t.parentTagId] = [];
        map[t.parentTagId].push(t.id);
      }
    });
    return map;
  }, [tagMap]);

  const fetchFilters = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/organizations/${orgId}/taxonomy/filter-options`);
      const f = res.data.filters || [];
      setFilters(f);
      const exp = {};
      f.forEach(tt => { exp[tt.id] = true; });
      setExpanded(exp);
    } catch {
      // Taxonomy not configured
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  useEffect(() => {
    setSelected(new Set(selectedTagIds));
  }, [selectedTagIds]);

  // Collect all descendant tag IDs of a given tag
  const getDescendantIds = useCallback((tagId) => {
    const ids = [];
    const children = childrenOf[tagId] || [];
    children.forEach(cid => {
      ids.push(cid);
      ids.push(...getDescendantIds(cid));
    });
    return ids;
  }, [childrenOf]);

  const toggleTag = (tagId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        // Deselecting: also remove all descendant selections
        next.delete(tagId);
        getDescendantIds(tagId).forEach(did => next.delete(did));
      } else {
        next.add(tagId);
      }
      onFilterChange?.(Array.from(next));
      return next;
    });
  };

  const clearAll = () => {
    setSelected(new Set());
    onFilterChange?.([]);
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // Determine which tags are visible for each tag type based on cascading logic
  const getVisibleTags = useCallback((tagType, tagTypeIndex) => {
    const typeTags = tagType.tags;

    if (tagTypeIndex === 0) {
      // First level: always show all
      return typeTags;
    }

    // For subsequent levels: only show tags whose parent is selected
    // in ANY of the preceding levels. If no parent is selected at all, show all.
    const prevLevelTypes = filters.slice(0, tagTypeIndex);
    const anyPrevSelected = prevLevelTypes.some(pt =>
      pt.tags.some(t => selected.has(t.id))
    );

    if (!anyPrevSelected) {
      // No parent-level selections: show all tags in this type
      return typeTags;
    }

    // Filter: only show tags whose parentTagId chain leads to a selected ancestor
    return typeTags.filter(tag => {
      // Walk up the parent chain to see if any ancestor is selected
      let current = tag;
      while (current && current.parentTagId) {
        if (selected.has(current.parentTagId)) return true;
        current = tagMap[current.parentTagId];
      }
      return false;
    });
  }, [filters, selected, tagMap]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  if (!filters.length) return null;

  return (
    <div className="space-y-3" data-testid="taxonomy-filter-sidebar">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold flex items-center gap-1.5 text-gray-700">
          <Filter className="w-3.5 h-3.5" /> Taxonomy Filters
        </span>
        {selected.size > 0 && (
          <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={clearAll} data-testid="clear-taxonomy-filters">
            <X className="w-3 h-3 mr-1" /> Clear ({selected.size})
          </Button>
        )}
      </div>

      {filters.map((tt, ttIndex) => {
        const isExpanded = expanded[tt.id];
        const visibleTags = getVisibleTags(tt, ttIndex);
        const selectedInType = visibleTags.filter(t => selected.has(t.id)).length;

        return (
          <div key={tt.id} data-testid={`taxonomy-filter-type-${tt.id}`}>
            <button
              className="flex items-center gap-1.5 w-full text-left py-1 text-sm font-medium text-gray-600 hover:text-gray-900"
              onClick={() => toggleExpand(tt.id)}
              data-testid={`taxonomy-filter-toggle-${tt.id}`}
            >
              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {tt.name}
              {selectedInType > 0 && (
                <Badge variant="default" className="text-[10px] px-1.5 h-4 ml-1">{selectedInType}</Badge>
              )}
            </button>
            {isExpanded && (
              <div className="ml-4 mt-1">
                {visibleTags.length > 0 ? (
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {visibleTags.map(tag => (
                      <div key={tag.id} className="flex items-center gap-2 py-0.5 min-w-0">
                        <Checkbox
                          id={`filter-${tag.id}`}
                          checked={selected.has(tag.id)}
                          onCheckedChange={() => toggleTag(tag.id)}
                          data-testid={`taxonomy-filter-checkbox-${tag.id}`}
                        />
                        <label htmlFor={`filter-${tag.id}`} className="text-sm cursor-pointer select-none whitespace-nowrap">
                          {tag.name}
                        </label>
                        {tag.courseCount > 0 && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 h-4">{tag.courseCount}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 py-1 italic">
                    {ttIndex > 0 ? 'Select a parent filter above to see options' : 'No tags defined'}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TaxonomyFilterSidebar;
